// src/components/eventos/views/EventoTVConteudo.js
// Visão TV — fullscreen, otimizada para projeção/telão.
// Rota: /tv/:clienteId

import React, { useState, useEffect, useRef } from 'react';
import { formatarDataCompleta, calcularDuracao } from './EventoPublicoConteudo';

/* ─── Ícones ──────────────────────────────────────────────────────────────── */
const ClockIcon = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
);
const MapPinIcon = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
    </svg>
);
const UserIcon = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
);
const MaximizeIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/>
        <path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/>
    </svg>
);
const MinimizeIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 3v3a2 2 0 0 1-2 2H3"/><path d="M21 8h-3a2 2 0 0 1-2-2V3"/>
        <path d="M3 16h3a2 2 0 0 1 2 2v3"/><path d="M16 21v-3a2 2 0 0 1 2-2h3"/>
    </svg>
);
const CalendarOffIcon = () => (
    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        <line x1="2" y1="2" x2="22" y2="22"/>
    </svg>
);

/* ─── Componente ──────────────────────────────────────────────────────────── */
const EventoTVConteudo = ({ evento, cliente }) => {
    const [horaAtual, setHoraAtual] = useState('');
    const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);

    useEffect(() => {
        const atualizar = () => {
            const n = new Date();
            setHoraAtual(`${String(n.getHours()).padStart(2,'0')}:${String(n.getMinutes()).padStart(2,'0')}`);
        };
        atualizar();
        const t = setInterval(atualizar, 1000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        const onChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen?.();
        } else {
            document.exitFullscreen?.();
        }
    };

    const tarefas = evento?.tarefas || [];
    const tarefaAtiva = tarefas.find(t => t.status === 'em_andamento');
    const idxAtiva = tarefas.indexOf(tarefaAtiva);
    const proximaTarefa = idxAtiva >= 0
        ? tarefas.slice(idxAtiva + 1).find(t => t.status === 'pendente' || !t.status)
        : tarefas.find(t => t.status === 'pendente' || !t.status);

    const dateInfo = evento ? formatarDataCompleta(evento.data) : null;
    const nomeCliente = cliente?.nome || '';
    const logoSrc = cliente?.logo_url || '/logo-horizontal.png';

    /* ── Sem evento ─────────────────────────────────────────────────────── */
    if (!evento) {
        return (
            <>
                <style>{`
                    @keyframes tv-pulse { 0%,100%{opacity:1} 50%{opacity:.35} }
                    @keyframes tv-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
                `}</style>
                <div style={{ position:'fixed', inset:0, background:'#f0faf6', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', fontFamily:"'Inter',-apple-system,sans-serif", overflow:'hidden' }}>
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:20, textAlign:'center' }}>
                        <img src={logoSrc} alt="Logo" style={{ height:'clamp(60px,8vw,120px)', objectFit:'contain', maxWidth:'clamp(200px,28vw,400px)' }} onError={e => { e.target.src='/logo-horizontal.png'; }} />
                        <div style={{ color:'#cbd5e1', animation:'tv-float 3s ease-in-out infinite', marginTop:8 }}>
                            <CalendarOffIcon />
                        </div>
                        <h1 style={{ margin:0, fontSize:'clamp(24px,3.5vw,42px)', fontWeight:800, color:'#0f172a', letterSpacing:'-0.03em' }}>
                            Nenhum evento ao vivo agora
                        </h1>
                        <p style={{ margin:0, fontSize:16, color:'#94a3b8', maxWidth:440, lineHeight:1.6 }}>
                            Quando um evento iniciar, a programação aparecerá aqui automaticamente.
                        </p>
                        <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'10px 22px', borderRadius:100, background:'#fff', border:'1px solid #e2e8f0', fontSize:13, color:'#94a3b8', fontWeight:500 }}>
                            <span style={{ width:7, height:7, borderRadius:'50%', background:'#cbd5e1', animation:'tv-pulse 1.5s ease-in-out infinite' }} />
                            Atualizando automaticamente
                        </div>
                    </div>
                    <button onClick={toggleFullscreen} style={{ position:'fixed', bottom:20, right:20, display:'inline-flex', alignItems:'center', gap:7, padding:'8px 16px', borderRadius:10, background:'#fff', border:'1px solid #e2e8f0', color:'#94a3b8', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor='#2CCB90'; e.currentTarget.style.color='#16a972'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor='#e2e8f0'; e.currentTarget.style.color='#94a3b8'; }}>
                        {isFullscreen ? <MinimizeIcon /> : <MaximizeIcon />}
                        {isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
                    </button>
                </div>
            </>
        );
    }

    /* ── Layout TV principal ─────────────────────────────────────────────── */
    const horaInicioAtiva = tarefaAtiva?.horaInicioReal || tarefaAtiva?.horaInicio;
    const horaFimAtiva    = tarefaAtiva?.horaFimReal    || tarefaAtiva?.horaFim;
    const horaInicioProx  = proximaTarefa?.horaInicio;
    const horaFimProx     = proximaTarefa?.horaFim;

    return (
        <>
            <style>{`
                @keyframes tv-pulse  { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:.4;transform:scale(.8);} }
                @keyframes tv-fadein { from{opacity:0;transform:translateY(14px);} to{opacity:1;transform:translateY(0);} }
                @keyframes tv-glow   {
                    0%,100% { box-shadow: 0 4px 24px rgba(44,203,144,0.12), 0 1px 6px rgba(0,0,0,0.05); }
                    50%     { box-shadow: 0 8px 40px rgba(44,203,144,0.22), 0 2px 10px rgba(0,0,0,0.07); }
                }
            `}</style>

            <div style={{ position:'fixed', inset:0, background:'#f0faf6', fontFamily:"'Inter',-apple-system,sans-serif", overflow:'hidden', display:'flex', flexDirection:'column' }}>

                {/* ── HEADER ─────────────────────────────────────────────── */}
                <header style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'clamp(16px,2.2vw,32px) clamp(32px,5vw,72px)', borderBottom:'1px solid #e2ede8', background:'rgba(255,255,255,0.8)', backdropFilter:'blur(16px)', WebkitBackdropFilter:'blur(16px)', flexShrink:0 }}>
                    <img src={logoSrc} alt="Logo" style={{ height:'clamp(80px,11vw,160px)', objectFit:'contain', maxWidth:'clamp(240px,34vw,480px)' }} onError={e => { e.target.src='/logo-horizontal.png'; }} />
                    <div style={{ fontSize:'clamp(32px,4vw,64px)', fontWeight:800, color:'#0f172a', letterSpacing:'0.04em', fontVariantNumeric:'tabular-nums' }}>
                        {horaAtual}
                    </div>
                </header>

                {/* ── BODY ───────────────────────────────────────────────── */}
                <main style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'center', padding:'clamp(24px,3vw,52px) clamp(32px,5vw,72px)', gap:'clamp(16px,2.2vw,32px)', overflow:'hidden' }}>

                    {/* Nome do evento + data */}
                    <div style={{ animation:'tv-fadein 0.45s ease both' }}>
                        <p style={{ margin:'0 0 6px', fontSize:'clamp(14px,1.3vw,20px)', fontWeight:600, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.09em' }}>
                            {dateInfo?.diaSemana && `${dateInfo.diaSemana}, `}{dateInfo?.data}
                        </p>
                        <h1 style={{ margin:0, fontSize:'clamp(28px,3.4vw,54px)', fontWeight:800, color:'#0f172a', letterSpacing:'-0.02em', lineHeight:1.15 }}>
                            {evento.titulo}
                        </h1>
                    </div>

                    {/* ── TAREFA ATUAL ────────────────────────────────────── */}
                    {tarefaAtiva ? (
                        <div style={{ background:'#ffffff', border:'1px solid #d1f0e0', borderLeft:'6px solid #2CCB90', borderRadius:20, padding:'clamp(24px,3vw,48px) clamp(28px,3.5vw,56px)', animation:'tv-glow 3s ease-in-out infinite, tv-fadein 0.45s ease 0.08s both', boxShadow:'0 4px 24px rgba(44,203,144,0.10)' }}>

                            {/* Badge */}
                            <div style={{ marginBottom:'clamp(12px,1.5vw,22px)' }}>
                                <span style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'7px 18px', borderRadius:100, background:'rgba(44,203,144,0.12)', border:'1px solid rgba(44,203,144,0.3)', color:'#16a972', fontSize:'clamp(12px,1.1vw,17px)', fontWeight:800, textTransform:'uppercase', letterSpacing:'0.08em' }}>
                                    <span style={{ width:9, height:9, borderRadius:'50%', background:'#2CCB90', animation:'tv-pulse 1.1s ease-in-out infinite' }} />
                                    Programação atual
                                </span>
                            </div>

                            {/* Título */}
                            <h2 style={{ margin:'0 0 clamp(16px,2vw,30px)', fontSize:'clamp(34px,5.5vw,88px)', fontWeight:800, color:'#0f172a', letterSpacing:'-0.03em', lineHeight:1.08 }}>
                                {tarefaAtiva.titulo}
                            </h2>

                            {/* Local e responsável */}
                            {(tarefaAtiva.local || tarefaAtiva.responsavel) && (
                                <div style={{ display:'flex', flexWrap:'wrap', gap:'clamp(16px,2vw,32px)', marginBottom:'clamp(14px,1.8vw,26px)' }}>
                                    {tarefaAtiva.local && (
                                        <div style={{ display:'inline-flex', alignItems:'center', gap:10, fontSize:'clamp(18px,1.9vw,30px)', color:'#475569', fontWeight:600 }}>
                                            <span style={{ color:'#2CCB90' }}><MapPinIcon size="1.8vw" /></span>
                                            {tarefaAtiva.local}
                                        </div>
                                    )}
                                    {tarefaAtiva.responsavel && (
                                        <div style={{ display:'inline-flex', alignItems:'center', gap:10, fontSize:'clamp(18px,1.9vw,30px)', color:'#475569', fontWeight:600 }}>
                                            <span style={{ color:'#2CCB90' }}><UserIcon size="1.8vw" /></span>
                                            {tarefaAtiva.responsavel}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Horário */}
                            {(horaInicioAtiva || horaFimAtiva) && (
                                <div style={{ display:'inline-flex', alignItems:'center', gap:12, padding:'clamp(10px,1.1vw,18px) clamp(18px,1.8vw,28px)', borderRadius:14, background:'rgba(44,203,144,0.08)', border:'1px solid rgba(44,203,144,0.25)', color:'#16a972', fontSize:'clamp(22px,2.5vw,40px)', fontWeight:800, letterSpacing:'0.02em', fontVariantNumeric:'tabular-nums' }}>
                                    <ClockIcon size="2.2vw" />
                                    {horaInicioAtiva}
                                    {horaFimAtiva && <> <span style={{ opacity:0.4, fontWeight:400 }}>→</span> {horaFimAtiva}</>}
                                    {calcularDuracao(horaInicioAtiva, horaFimAtiva) && (
                                        <span style={{ opacity:0.5, fontSize:'clamp(14px,1.3vw,22px)', fontWeight:600, marginLeft:6 }}>
                                            · {calcularDuracao(horaInicioAtiva, horaFimAtiva)}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{ background:'#ffffff', border:'1px solid #e8edf2', borderRadius:20, padding:'clamp(24px,3vw,48px) clamp(28px,3.5vw,56px)', animation:'tv-fadein 0.45s ease 0.08s both', boxShadow:'0 2px 8px rgba(0,0,0,0.04)' }}>
                            <p style={{ margin:'0 0 10px', fontSize:'clamp(13px,1.1vw,17px)', fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.09em' }}>Programação atual</p>
                            <p style={{ margin:0, fontSize:'clamp(26px,3.5vw,52px)', fontWeight:700, color:'#cbd5e1' }}>Nenhum item em andamento</p>
                        </div>
                    )}

                    {/* ── PRÓXIMA TAREFA ──────────────────────────────────── */}
                    {proximaTarefa && (
                        <div style={{ display:'flex', alignItems:'center', gap:'clamp(16px,2vw,28px)', padding:'clamp(16px,1.9vw,28px) clamp(24px,2.8vw,44px)', background:'#ffffff', border:'1px solid #e8edf2', borderRadius:16, animation:'tv-fadein 0.45s ease 0.16s both', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
                            <div style={{ flexShrink:0 }}>
                                <p style={{ margin:0, fontSize:'clamp(12px,1vw,16px)', fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.09em' }}>A seguir</p>
                            </div>
                            <div style={{ width:1, height:36, background:'#e8edf2', flexShrink:0 }} />
                            <div style={{ flex:1, minWidth:0 }}>
                                <p style={{ margin:0, fontSize:'clamp(20px,2.4vw,38px)', fontWeight:700, color:'#334155', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                                    {proximaTarefa.titulo}
                                </p>
                                {proximaTarefa.local && (
                                    <p style={{ margin:'4px 0 0', fontSize:'clamp(13px,1.2vw,19px)', color:'#94a3b8', fontWeight:500, display:'flex', alignItems:'center', gap:6 }}>
                                        <MapPinIcon size="1.1vw" /> {proximaTarefa.local}
                                    </p>
                                )}
                            </div>
                            {(horaInicioProx || horaFimProx) && (
                                <div style={{ display:'inline-flex', alignItems:'center', gap:10, padding:'clamp(8px,0.8vw,13px) clamp(14px,1.4vw,22px)', borderRadius:12, background:'#f8fafc', border:'1px solid #e2e8f0', color:'#64748b', fontSize:'clamp(18px,2vw,32px)', fontWeight:700, letterSpacing:'0.02em', fontVariantNumeric:'tabular-nums', flexShrink:0 }}>
                                    <ClockIcon size="1.7vw" />
                                    {horaInicioProx}{horaFimProx && ` → ${horaFimProx}`}
                                </div>
                            )}
                        </div>
                    )}
                </main>

                {/* ── Botão fullscreen ────────────────────────────────────── */}
                <button
                    onClick={toggleFullscreen}
                    style={{ position:'fixed', bottom:20, right:20, zIndex:50, display:'inline-flex', alignItems:'center', gap:7, padding:'8px 16px', borderRadius:10, background:'#fff', border:'1px solid #e2e8f0', color:'#94a3b8', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor='#2CCB90'; e.currentTarget.style.color='#16a972'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor='#e2e8f0'; e.currentTarget.style.color='#94a3b8'; }}
                >
                    {isFullscreen ? <MinimizeIcon /> : <MaximizeIcon />}
                    {isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
                </button>
            </div>
        </>
    );
};

export default EventoTVConteudo;
